/**
 * Display of a measurement's date (#1653).
 *
 * An ICP analysis known only by its report date has no time of day: the API
 * stores its values at midnight and sends `measuredDate: 'YYYY-MM-DD'`.
 * Formatting `measuredAt` in the browser's time zone would show a made-up
 * time ("25.08.2026, 02:00" in Europe/Berlin) or even the previous day (in
 * the Americas), so such values are formatted from the calendar date alone.
 * Values measured at a real time keep date + time in the local time zone.
 */
export interface DatedMeasurement {
  readonly measuredAt: string;
  /** 'YYYY-MM-DD' for a value without a time of day, else null/absent. */
  readonly measuredDate?: string | null;
}

/** 'medium' → "25 Aug 2026, 14:30" (history) · 'short' → "Aug 25, 2026, 02:30 PM" (journal cards). */
export type MeasurementDateStyle = 'medium' | 'short';

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

const WITH_TIME: Record<MeasurementDateStyle, Intl.DateTimeFormatOptions> = {
  medium: { dateStyle: 'medium', timeStyle: 'short' },
  short: { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' },
};

const DATE_ONLY_OPTIONS: Record<MeasurementDateStyle, Intl.DateTimeFormatOptions> = {
  medium: { dateStyle: 'medium', timeZone: 'UTC' },
  short: { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' },
};

/** The calendar date as UTC midnight, or null when `value` is not a valid 'YYYY-MM-DD'. */
export function dateOnlyToUtc(value: string | null | undefined): Date | null {
  const match = value ? DATE_ONLY.exec(value) : null;
  if (!match) return null;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  return Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== value ? null : date;
}

/** True when the measurement has no time of day (show its date only). */
export function isDateOnly(measurement: DatedMeasurement): boolean {
  return dateOnlyToUtc(measurement.measuredDate) !== null;
}

/** Date (+ time when the value has one) of a measurement, for display. */
export function formatMeasuredAt(
  measurement: DatedMeasurement,
  language: string | undefined,
  style: MeasurementDateStyle = 'medium',
  /** Time zone of a timed value; the browser's by default (tests pin it). */
  timeZone?: string,
): string {
  const locale = language || undefined;
  const dateOnly = dateOnlyToUtc(measurement.measuredDate);
  if (dateOnly) return new Intl.DateTimeFormat(locale, DATE_ONLY_OPTIONS[style]).format(dateOnly);
  const date = new Date(measurement.measuredAt);
  if (Number.isNaN(date.getTime())) return measurement.measuredAt;
  return new Intl.DateTimeFormat(locale, { ...WITH_TIME[style], timeZone }).format(date);
}

/** Machine-readable value for <time dateTime>: the date alone for a date-only value. */
export function measuredAtDateTimeAttr(measurement: DatedMeasurement): string {
  return dateOnlyToUtc(measurement.measuredDate) ? (measurement.measuredDate as string) : measurement.measuredAt;
}
